// Spaces Light v3: pure, DOM-free markup-building helpers (skeleton/unit card HTML,
// HTML escaping, and the filter/sort comparators) used by _base.js. Split out into its
// own module so neither file grows past a reasonable line-count/complexity budget;
// re-imported into _base.js via the webpack-bundled ES module graph (see
// src/scripts/components.js, which only imports _base.js directly).

/**
 * Inline calculator glyph markup (all unit rows are client-rendered now, so this is
 * the sole source of truth for the icon -- previously mirrored a server-rendered
 * _icon-calculator.twig partial, which no longer exists).
 */
export var CALCULATOR_ICON_HTML = '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9993 2.66659H3.99935V13.3333H11.9993V2.66659ZM3.99935 1.33325C3.26297 1.33325 2.66602 1.93021 2.66602 2.66659V13.3333C2.66602 14.0696 3.26297 14.6666 3.99935 14.6666H11.9993C12.7357 14.6666 13.3327 14.0696 13.3327 13.3333V2.66659C13.3327 1.93021 12.7357 1.33325 11.9993 1.33325H3.99935Z" fill="currentColor"></path>' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9993 6.66659H3.99935V5.33325H11.9993V6.66659Z" fill="currentColor"></path>' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M11.9993 9.33325H3.99935V7.99992H11.9993V9.33325Z" fill="currentColor"></path>' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M9.99935 11.9999H3.99935V10.6666H9.99935V11.9999Z" fill="currentColor"></path>' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M10.666 5.99992L10.666 13.9999H9.33268L9.33268 5.99992H10.666Z" fill="currentColor"></path>' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M7.33268 5.99992V13.9999H5.99935L5.99935 5.99992H7.33268Z" fill="currentColor"></path>' +
  '</svg>'

/**
 * Build the markup for `count` skeleton loading cards, shown in place of the real
 * unit cards while a fetch is in flight. Matches the server-rendered skeleton markup
 * in _standard-v3.twig exactly (same classes/bar structure) so the initial paint and
 * every subsequent fetch look identical -- no layout shift when real cards swap in.
 *
 * @param {number} count How many skeleton cards to render (normally config.perPage).
 * @return {string} The skeleton cards markup.
 */
export function renderSkeletonHtml (count) {
  var cardHtml =
    '<div class="spaceslight-v3-unit-skeleton" aria-hidden="true">' +
      '<span class="spaceslight-v3-unit-skeleton-bar spaceslight-v3-unit-skeleton-bar--number"></span>' +
      '<span class="spaceslight-v3-unit-skeleton-bar spaceslight-v3-unit-skeleton-bar--floorplan"></span>' +
      '<span class="spaceslight-v3-unit-skeleton-bar spaceslight-v3-unit-skeleton-bar--details"></span>' +
      '<span class="spaceslight-v3-unit-skeleton-bar spaceslight-v3-unit-skeleton-bar--price"></span>' +
      '<span class="spaceslight-v3-unit-skeleton-bar spaceslight-v3-unit-skeleton-bar--price"></span>' +
      '<span class="spaceslight-v3-unit-skeleton-bar spaceslight-v3-unit-skeleton-bar--available"></span>' +
      '<span class="spaceslight-v3-unit-skeleton-bar spaceslight-v3-unit-skeleton-bar--cta"></span>' +
    '</div>'

  return new Array(count || 1).fill(/* safe */ cardHtml).join('')
}

/**
 * Check whether a unit matches the selected bedroom filter value.
 *
 * Handles both standard numeric bedroom-count values (e.g. "2") and custom
 * CSS-class-based room filters configured via Spaces' "Custom Room Filters"
 * repeater (ACF), whose `<option>` value is `custom:<dot-delimited-classes>`
 * (e.g. "custom:2bed.penthouse") -- matched by requiring every listed class
 * be present in the unit's `css_classes` array (AND), mirroring the SPACES
 * REST controller's css_class_groups semantics.
 *
 * @param {Object} unit The unit object.
 * @param {string} beds The selected bedroom filter value (empty = all).
 * @return {boolean} Whether the unit matches.
 */
export function matchesBedroom (unit, beds) {
  if (!beds) {
    return true
  }

  if (String(beds).indexOf('custom:') === 0) {
    const requiredClasses = String(beds).slice('custom:'.length).split('.').filter(Boolean)
    const unitClasses = unit.css_classes || []
    return requiredClasses.every(function (cls) {
      return unitClasses.indexOf(cls) !== -1
    })
  }

  return String(unit.bedroom_count) === String(beds)
}

/**
 * Check whether a unit matches the selected move-in date filter, replicating the
 * REST controller's `available_on <= selected date` semantics (ISO YYYY-MM-DD
 * strings compare correctly with a plain string comparison).
 *
 * @param {Object} unit The unit object.
 * @param {string} availableOn The selected move-in date filter value (empty = all).
 * @return {boolean} Whether the unit matches.
 */
export function matchesMoveinDate (unit, availableOn) {
  if (!availableOn) {
    return true
  }

  if (!unit.available_on) {
    return false
  }

  return String(unit.available_on) <= availableOn
}

/**
 * Coerce a unit's raw price to a sortable number (missing/invalid -> 0).
 *
 * @param {*} value The raw price value.
 * @return {number} A sortable number.
 */
export function getSortablePrice (value) {
  const num = Number(value)
  return Number.isFinite(num) ? num : 0
}

/**
 * Coerce a unit's `date_sort_val` (epoch seconds, or `false` when absent) to a
 * sortable number -- units with no date sort to the end regardless of direction.
 *
 * @param {Object} unit The unit object.
 * @return {number} A sortable number.
 */
export function getSortableDate (unit) {
  const value = unit.date_sort_val
  return (value === false || value === null || value === undefined) ? Number.MAX_SAFE_INTEGER : Number(value)
}

/**
 * Compare two units for the given sort key (mirrors the REST API's `pr_asc`/
 * `pr_desc`/`dt_asc`/`dt_desc` sort keys, computed client-side instead).
 *
 * @param {Object} a First unit.
 * @param {Object} b Second unit.
 * @param {string} sort The sort key.
 * @return {number} A comparator result.
 */
export function compareUnits (a, b, sort) {
  switch (sort) {
    case 'pr_desc':
      return getSortablePrice(b.price) - getSortablePrice(a.price)
    case 'dt_asc':
      return getSortableDate(a) - getSortableDate(b)
    case 'dt_desc':
      return getSortableDate(b) - getSortableDate(a)
    case 'pr_asc':
    default:
      return getSortablePrice(a.price) - getSortablePrice(b.price)
  }
}

/**
 * Escape a string for safe interpolation into HTML markup.
 *
 * @param {*} value The value to escape.
 * @return {string} The escaped string.
 */
export function escapeAsHtml (value) {
  // The chained String.replace() calls below are this function's own encoding
  // implementation, not unescaped output, so they're intentionally exempt
  // from the xss/no-mixed-html check that applies to callers of this helper.
  return /* safe */ String(value === null || value === undefined ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/\u0027/g, '&#39;')
}

/**
 * Build the markup for a unit row's tag chips (its own custom tags plus its floor
 * plan's tags, and the "Special Offer" chip when applicable).
 *
 * Extracted out of renderUnitHtml() to keep that function's line count within the
 * configured threshold.
 *
 * @param {Object} unit The unit object returned by the REST API.
 * @param {Object} floorPlan unit.floor_plan (or {} when absent).
 * @return {string} The tag chips markup.
 */
function buildUnitTagsHtml (unit, floorPlan) {
  const unitTags = (unit.tags || []).concat(floorPlan.tags || [])
  let tagsHtml = ''
  for (const unitTag of unitTags) {
    tagsHtml += '<span class="spaces-tag spaces-tag--brand-1">' + escapeAsHtml(unitTag.name) + '</span>'
  }
  const specialTagHtml = unit.specials
    ? '<span class="spaces-tag spaces-tag--brand-2" aria-label="Unit has special offer">Special Offer</span>'
    : ''

  return specialTagHtml + tagsHtml
}

/**
 * Build a unit row's price-related markup: the base-rent price, the total-monthly
 * price (with its Sightmap expense-calculator trigger/info icon), and the "Apply"
 * CTA link.
 *
 * Extracted out of renderUnitHtml() to keep that function's line count within the
 * configured threshold.
 *
 * @param {Object} unit The unit object returned by the REST API.
 * @param {Object} config The instance's bootstrap config.
 * @param {string} displayUrl config.displayUrl (or '' when absent).
 * @return {{priceBaseHtml: string, priceTotalHtml: string, priceTotalCalculatorIconHtml: string, applyCtaHtml: string}}
 */
function buildUnitPricingHtml (unit, config, displayUrl) {
  const priceBaseHtml = (!config.hidePa && !config.hideStartingAt)
    ? '<span class="spaces__mobile-only">Base Rent: </span>' + escapeAsHtml(unit.price_string)
    : 'Contact'

  const priceTotalHtml = (!config.hidePa && !config.hideStartingAt)
    ? '<span class="spaces__mobile-only">Total Monthly Leasing: </span><span class="spaces-text-price">' + escapeAsHtml(unit.price_with_fees_string) + '</span><span class="spaces__desktop-only">/mo</span>'
    : 'Contact'

  const applyCtaHtml = (!config.hidePa && !config.hideApplyCta && unit.apply_provider !== 'none' && !unit.hide_apply_link)
    ? '<a class="spaces-button spaces-button-fill-brand" href="' + escapeAsHtml(displayUrl + unit.apply_url) + '" target="_blank" data-spaces-unit-id="' + escapeAsHtml(unit.id) + '" aria-label="Apply for unit ' + escapeAsHtml(unit.unit_number) + '"><span class="spaces-button-label">Apply</span></a>'
    : ''

  const priceTotalCalculatorIconHtml = (!config.hidePa && !config.hideStartingAt)
    ? (config.enableCalculator
      ? '<button type="button" class="spaceslight-v3-calculator-icon spaceslight-v3-calculator-trigger" data-spaceslight-calculator-trigger' +
        ' data-spaceslight-tooltip="Expense Calculator"' +
        ' data-spaces-unit-id="' + escapeAsHtml(unit.id) + '"' +
        ' data-spaces-unit-number="' + escapeAsHtml(unit.unit_number) + '"' +
        ' data-spaces-calculator-lease-term="' + escapeAsHtml(unit.price_lease_term) + '"' +
        ' data-spaces-calculator-date="' + escapeAsHtml(unit.available_on) + '"' +
        ' aria-label="View expense calculator for unit ' + escapeAsHtml(unit.unit_number) + '" title="View expense calculator">' +
        CALCULATOR_ICON_HTML +
        '</button>'
      : '<span class="spaceslight-v3-calculator-icon" aria-label="Total includes additional fees" title="Total includes additional fees"><span class="ovation_icon_exclamation" aria-hidden="true"></span></span>')
    : ''

  return { priceBaseHtml, priceTotalHtml, priceTotalCalculatorIconHtml, applyCtaHtml }
}

/**
 * Build the markup for a single unit row. All units (every page, including the
 * first) are client-rendered now; this is the sole source of truth for the markup
 * (there is no longer a server-rendered _unit-v3.twig to mirror).
 *
 * @param {Object} unit The unit object returned by the REST API.
 * @param {Object} config The instance's bootstrap config.
 * @return {string} The unit row markup.
 */
export function renderUnitHtml (unit, config) {
  const displayUrl = config.displayUrl || ''
  const floorPlan = unit.floor_plan || {}
  const bedroomLabelHtml = (floorPlan.bedroom_count < 1) ? 'Studio' : escapeAsHtml(floorPlan.bedroom_count_string_short)
  const tagsHtml = buildUnitTagsHtml(unit, floorPlan)
  const { priceBaseHtml, priceTotalHtml, priceTotalCalculatorIconHtml, applyCtaHtml } = buildUnitPricingHtml(unit, config, displayUrl)
  const detailTargetHtml = config.sameTabDetail ? '' : ' target="_blank"'

  return (
    '<article class="spaces__unit spaces-unit-light ' + escapeAsHtml(unit.css_class_string) + '" aria-label="Unit ' + escapeAsHtml(unit.unit_number) + '">' +
      '<div class="spaces__unit-number spaces__unit-column">' +
        '<a href="' + escapeAsHtml(displayUrl + unit.detail_link) + '"' + detailTargetHtml + ' data-spaceslight-tooltip="' + escapeAsHtml(floorPlan.name) + ' Floor Plan">' +
          '<span class="spaces__label spaces__label-unit-number" aria-label="Unit Number"><span class="spaces__mobile-only">Unit </span>' + escapeAsHtml(unit.unit_number) + '</span>' +
        '</a>' +
        '<div class="spaces-tags-wrapper">' + tagsHtml + '</div>' +
      '</div>' +
      '<div class="spaces__unit-floorplan spaces__unit-column">' +
        '<a class="spaces__unit-floorplan-link" href="' + escapeAsHtml(displayUrl + floorPlan.detail_link) + '" target="_blank">' +
          '<span class="spaces__mobile-only">Floor Plan: </span>' +
          '<span class="spaces__unit-floorplan-name">' + escapeAsHtml(floorPlan.name) + '</span>' +
        '</a>' +
      '</div>' +
      '<div class="spaces__unit-plan-info spaces__unit-column" data-spaces-control="unit-plan-info">' +
        '<span class="spaces__plan__attributes-bedcount spaces__plan__attribute" data-spaces-control="plan-bed-count">' +
          '<span class="spaces__label" aria-label="Bedroom Count">' + bedroomLabelHtml + '</span>' +
        '</span>' +
        '<span class="spaces__plan__attributes-bathcount spaces__plan__attribute" data-spaces-control="plan-bath-count">' +
          '<span class="spaces__label" aria-label="Bathroom Count">' + escapeAsHtml(floorPlan.bathroom_count_string_short) + '</span>' +
        '</span>' +
        '<span class="spaces__plan__attributes-area spaces__plan__attribute" data-spaces-control="plan-area">' +
          '<span class="spaces__label-icon__label spaces__label" aria-label="Unit Area">' + escapeAsHtml(Math.round(unit.area).toLocaleString()) + ' Sq. Ft.</span>' +
        '</span>' +
      '</div>' +
      '<div class="spaces__unit-pricing-base spaces__unit-column" data-spaces-control="unit-availability">' +
        '<span class="spaces__label spaces__label-price" aria-label="Price">' + priceBaseHtml + '</span>' +
      '</div>' +
      '<div class="spaces__unit-pricing-total spaces__unit-column" data-spaces-control="unit-availability">' +
        '<span class="spaces__label spaces__label-price" aria-label="Price">' + priceTotalHtml + priceTotalCalculatorIconHtml + '</span>' +
      '</div>' +
      '<div class="spaces__unit-availability spaces__unit-column" data-spaces-control="unit-availability">' +
        '<span class="spaces__label spaces__label-available-on" aria-label="Available Date">' +
          '<span class="spaces__mobile-only">Available: </span>' + escapeAsHtml(unit.available_on_string) +
        '</span>' +
      '</div>' +
      '<div class="spaces__unit-column spaces__unit-column-ctas">' +
        applyCtaHtml +
        '<a class="spaces-button spaces-button-content-brand-1-on-ui-bordered" href="' + escapeAsHtml(displayUrl + unit.detail_link) + '" target="_blank" aria-label="View details for unit ' + escapeAsHtml(unit.unit_number) + '">' +
          '<span class="spaces-button-label spaces-text-p-default">View Details</span>' +
        '</a>' +
      '</div>' +
    '</article>'
  )
}
